import getRecaptchaToken from '../helpers/getRecaptchaToken';
import AppContext from './context/AppContext';
import { Form, Image, Row } from 'react-bootstrap';
import PropTypes from 'prop-types';
import React, { useContext, useState, useCallback } from 'react';
import '../styles/QuestionsAndAnswersForm.css';
import LoadingFallback from './LoadingFallback';
import { ToastContext } from './context/ToastContext';
import { FaUser } from 'react-icons/fa';

const IMAGE_FILE_TYPE_PREFIX = 'image/';
const COMMON_CLASSNAME = 'mb-3 secondary-form--group';

/**
 * A form for submitting questions.
 * @component
 */
const QuestionsAndAnswersForm = ({
    productId,
    hidden = false,
    onQuestionSubmit = () => { }
}) => {
    /** Contexts */
    const { appContext } = useContext(AppContext);

    /** States */
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [title, setTitle] = useState('');
    const [question, setQuestion] = useState('');
    const [attachmentsPreview, setAttachmentsPreview] = useState(null);
    const [imageFiles, setImageFiles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isTyping, setIsTyping] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    const { sendMessage } = useContext(ToastContext);

    /** State Handlers */
    const onChangeName = useCallback((e) => setName(e.target.value), []);
    const onChangeEmail = useCallback((e) => setEmail(e.target.value), []);
    const onChangeQuestion = useCallback((e) => {
        setQuestion(e.target.value);
        if (!isTyping && e.target.value.length > 0) {
            setIsTyping(true);
        }
    }, [isTyping]);
    const onChangeTitle = useCallback((e) => setTitle(e.target.value), []);

    const handleFocus = useCallback(() => setIsFocused(true), []);
    const handleBlur = useCallback(() => {
        if (question.length === 0) {
            setIsFocused(false);
        }
    }, [question?.length]);

    /** Form Event Handlers */
    /**
     * Handle form submission.
     * @param {Object} e The event object.
     */
    const onSubmit = useCallback(async (e) => {
        e.preventDefault();
        e.stopPropagation();
        await submitQuestion();
    }, [submitQuestion]);

    /**
     * Submit a question.
     */
    const submitQuestion = async () => {
        try {
            setLoading(true);
            const url = new URL('/api/questions', process.env.APP_URL);
            const formData = new FormData();
            formData.append('name', name);
            formData.append('email', email);
            formData.append('title', title);
            formData.append('question', question);
            formData.append('productId', productId);
            if (imageFiles.length > 0) {
                for (const file of imageFiles) {
                    formData.append('images', file);
                }
            }
            const response = await fetch(url, {
                method: 'POST',
                body: formData,
                headers: {
                    'x-grecaptcha-token': await getRecaptchaToken(appContext.config.recaptchaSiteKey),
                },
            });
            if (!response.ok) {
                throw new Error('API request failed: bad response.');
            }
            const data = await response.json();
            if (!data.question) {
                throw new Error('Did not get the expected response from server.');
            }
            setAttachmentsPreview(null);
            setImageFiles([]);
            // Reset form.
            setName('');
            setEmail('');
            setQuestion('');
            setTitle('');
            setIsTyping(false);
            // Call function to execute after submission.
            onQuestionSubmit();
            sendMessage('Question saved', 'success');
        } catch (e) {
            sendMessage('Question not saved', 'danger');
        } finally {
            setLoading(false);
        }
    };

    /** Image Event Handlers */
    const handleUploadImageChange = useCallback((e) => {
        try {
            if (!e.target.files.length) {
                throw new Error('No uploaded image available to preview');
            }

            const imgFiles = e.target.files;
            const attachmentsPreview = [];
            for (const file of imgFiles) {
                if (file.type.startsWith(IMAGE_FILE_TYPE_PREFIX)) {
                    attachmentsPreview.push({
                        isImage: true,
                        base64: URL.createObjectURL(file),
                    });
                } else {
                    attachmentsPreview.push({
                        isImage: false,
                        fileName: file.name
                    });
                }
            }

            setAttachmentsPreview(attachmentsPreview);
            setImageFiles(imgFiles);
        } catch (e) {
            sendMessage('Error uploading images', 'danger');
        }
    }, [setAttachmentsPreview, setImageFiles]);

    /** UI */
    return (
        <>
            {
                loading ? <LoadingFallback /> :
                    <Form
                        className={`mb-5${hidden ? ' d-none' : ''}`}
                        id={'ask-question-form'}
                        onSubmit={onSubmit}
                    >
                        <Form.Group
                            className={'mb-3'}
                            controlId={'ask-question-form--question'}>
                            <div
                                className={'question-input--container'}>
                                <div
                                    className={'user-icon--container'}>
                                    <FaUser
                                        className={'question-user--icon'}
                                    />
                                </div>
                                <Form.Control
                                    type={'text'}
                                    value={question}
                                    onChange={onChangeQuestion}
                                    placeholder={'Ask a question...'}
                                    className={'question--input'}
                                    onFocus={handleFocus}
                                    onBlur={handleBlur}
                                />
                            </div>
                            <div
                                className={'characters--remaining'}>
                                {115 - question.length} {' characters remaining'}
                            </div>
                            <div
                                className={'continue-button--container'}>
                                <button
                                    className={'ask-question-form--continue-button'}
                                    type={'button'}
                                    onClick={() => setIsTyping(true)}
                                    disabled={isTyping}
                                >
                                    Continue
                                </button>
                            </div>
                        </Form.Group>

                        {isTyping && (
                            <>
                                <Form.Group
                                    className={COMMON_CLASSNAME}
                                    controlId={'ask-question-form--name'}>
                                    <Form.Label>Name</Form.Label>
                                    <Form.Control
                                        value={name}
                                        onChange={onChangeName} />
                                </Form.Group>
                                <Form.Group
                                    className={COMMON_CLASSNAME}
                                    controlId={'ask-question-form--email'}>
                                    <Form.Label>Email</Form.Label>
                                    <Form.Control
                                        value={email}
                                        onChange={onChangeEmail} />
                                </Form.Group>
                                <Form.Group
                                    className={COMMON_CLASSNAME}>
                                    <Form.Label>Title</Form.Label>
                                    <Form.Control
                                        value={title}
                                        onChange={onChangeTitle}
                                    />
                                </Form.Group>
                                <Form.Group
                                    className={COMMON_CLASSNAME}
                                    controlId={'answerForm.AnswerImages'}>
                                    <Form.Label>Upload Attachments (Optional)</Form.Label>
                                    {attachmentsPreview && (<Row
                                        xs={1}
                                        md={3}
                                        lg={4}
                                    >
                                        {
                                            attachmentsPreview.map(({ isImage, base64, fileName }, index) => {
                                                return (
                                                    <div
                                                        key={`${index}-${fileName || ''}`}
                                                    >
                                                        {isImage ? (
                                                            <Image
                                                                src={base64}
                                                                alt={''}
                                                                width={'100%'}
                                                                rounded
                                                            />
                                                        ) : (
                                                            <div>{fileName}</div>
                                                        )}
                                                    </div>
                                                );
                                            })
                                        }
                                    </Row>)}
                                    <br />
                                    <Form.Control
                                        type={'file'}
                                        multiple
                                        onChange={handleUploadImageChange}
                                        style={{ marginBottom: '10px' }} />
                                </Form.Group>
                                <button
                                    className={'ask-question-form--submit-button'}
                                    type={'submit'}>
                                    Submit
                                </button>
                            </>
                        )}
                    </Form>
            }
        </>
    );
};

/** Prop Types */
QuestionsAndAnswersForm.propTypes = {
    /** The product ID. */
    productId: PropTypes.number.isRequired,
    /** Indicate whether the form should be shown or not. */
    hidden: PropTypes.bool,
    /** Callback function to call after a question is submitted. */
    onQuestionSubmit: PropTypes.func,
};

export default QuestionsAndAnswersForm;
